import { defineMessages } from "react-intl"
import { BulkActionDefinition, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { CREDIT_MEMO_STATUS } from "../../../../invoice/credit-memo/CreditMemoStatus"
import { BULK_OPERATION_TYPE } from "../../../../utils/automation/bulk/constants"
import { PREFERENCE_TYPE } from "../../constants"

export enum CreditMemosColumnIds {
    IDENTIFIER = "identifier",
    CONTACT = "contact",
    NOTES = "notesKeyword",
    CREATED_AT_DATE = "createdAt",
    CAPTURED_AT_DATE = "capturedDate",
    STATUS = "status",
    AMOUNT = "amount",
    REMAINING_AMOUNT = "remainingAmount",
    REFUNDED_AMOUNT = "refundedAmount",
    ARCHIVED = "isArchived",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: CreditMemosColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            modal: require("../../../../common/listing/generic-listing/edit-modals/CreditMemoEditModal")
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: CreditMemosColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: CreditMemosColumnIds.NOTES,
        sortId: "notesKeywords",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: CreditMemosColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: CreditMemosColumnIds.CAPTURED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: CreditMemosColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                [CREDIT_MEMO_STATUS.AVAILABLE]: {
                    message: {
                        id: "generic-listing.credit-memos.badge.available",
                        defaultMessage: "Available",
                    },
                    type: "primary",
                },
                [CREDIT_MEMO_STATUS.PENDING]: {
                    message: {
                        id: "generic-listing.credit-memos.badge.pending",
                        defaultMessage: "Pending",
                    },
                    type: "info",
                },
                [CREDIT_MEMO_STATUS.PARTIALLY_APPLIED]: {
                    message: {
                        id: "generic-listing.credit-memos.badge.partially-applied",
                        defaultMessage: "Partially applied",
                    },
                    type: "warning",
                },
                [CREDIT_MEMO_STATUS.FULLY_APPLIED]: {
                    message: {
                        id: "generic-listing.credit-memos.badge.fully-applied",
                        defaultMessage: "Fully applied",
                    },
                    type: "success",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: CreditMemosColumnIds.AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalAmount",
            type: TotalType.CURRENCY,
            //Show positive totals on the credit memos listing
            parseValue: (value: string) => {
                if (parseFloat(value) < 0) {
                    value = value[0] == "-" ? value.slice(1) : value
                }
                return value
            }
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: CreditMemosColumnIds.REMAINING_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyRemainingAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: CreditMemosColumnIds.REFUNDED_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: CreditMemosColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
]

const headerMessages = defineMessages({
    [CreditMemosColumnIds.IDENTIFIER]: {
        id: "generic-listing.credit-memos.table-header.identifier",
        defaultMessage: "ID",
    },
    [CreditMemosColumnIds.CONTACT]: {
        id: "generic-listing.credit-memos.table-header.contact",
        defaultMessage: "Contact",
    },
    [CreditMemosColumnIds.NOTES]: {
        id: "generic-listing.credit-memos.table-header.notes",
        defaultMessage: "Notes",
    },
    [CreditMemosColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.credit-memos.table-header.created-at-date",
        defaultMessage: "Created date",
    },
    [CreditMemosColumnIds.CAPTURED_AT_DATE]: {
        id: "generic-listing.credit-memos.table-header.captured-at-date",
        defaultMessage: "Captured date",
    },
    [CreditMemosColumnIds.STATUS]: {
        id: "generic-listing.credit-memos.table-header.status",
        defaultMessage: "Status",
    },
    [CreditMemosColumnIds.AMOUNT]: {
        id: "generic-listing.credit-memos.table-header.amount",
        defaultMessage: "Amount",
    },
    [CreditMemosColumnIds.REMAINING_AMOUNT]: {
        id: "generic-listing.credit-memos.table-header.remaining-amount",
        defaultMessage: "Remaining amount",
    },
    [CreditMemosColumnIds.REFUNDED_AMOUNT]: {
        id: "generic-listing.credit-memos.table-header.refunded-amount",
        defaultMessage: "Refunded amount",
    },
    [CreditMemosColumnIds.ARCHIVED]: {
        id: "generic-listing.credit-memos.table-header.archived",
        defaultMessage: "Archived",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.EDIT_CREDIT_MEMO_LOCATION,
        message: {
            id: "generic-listing.bulk-actions.edit-credit-memos-location",
            defaultMessage: "Edit credit memos location",
        },
        icon: "map-marker-alt",
    }
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.CREDIT_MEMOS,
    columnDefinitions: columnDefinitions,
    headerMessages: headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_CREDIT_MEMOS,
    searchPlaceholder: {
        id: "generic-listing.credit-memos.search-placeholder",
        defaultMessage: "Search a credit memo...",
    },
    displayString: {
        id: "generic-listing.credit-memos.display-string",
        defaultMessage: "Credit Memos",
    },
    searchObjectType: ObjectType.CREDIT_MEMOS,
    bulkActionDefinitions,
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/CreditMemoCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/credit-memos.ts